export const normalizeRegisterPayload = ({
  data,
  roleId,
  dob,
  emailVerifyString,
  selectedResume,
}) => {
  let payload = { ...data };

  payload.role_id = roleId;
  payload.step_number = "step_1";
  payload.type = "register";
  payload.role_type = roleId;
  payload.email_verify_string = emailVerifyString;

  if (payload.subarea) {
    payload.subareas = payload.subarea;
  }

  if (payload.mainArea) {
    payload.domains = payload.mainArea;
  }

  payload = normalizeBooleanFields(payload);
  payload = normalizeDateFields(payload, dob);
  payload = cleanupPayloadFields(payload);

  return handleResumeInPayload(payload, selectedResume);
};

const normalizeBooleanFields = (payload) => {
  const booleanKeys = [
    "mn_open_to_talk",
    "is_privacy",
    "is_terms",
    "my_consent",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
    "lgbtq_discussion",
    "is_disclaimer",
    "training_video",
  ];

  const newPayload = { ...payload };
  for (const key of booleanKeys) {
    if (key in newPayload) {
      newPayload[key] = newPayload[key] ? 1 : 0;
    }
  }
  return newPayload;
};

const normalizeDateFields = (payload, dob) => {
  const newPayload = { ...payload };
  if ("dob" in newPayload) {
    newPayload.dob = newPayload.dob ? dob : "";
  }

  if (newPayload.availability) {
    newPayload.availability = newPayload.availability ? 2 : 1;
  } else {
    newPayload.availability = 1;
    newPayload.from_date = null;
    newPayload.to_date = null;
  }

  // Handle board_roles repeater field - clean up based on "Currently Working" flag
  if (newPayload.board_roles && Array.isArray(newPayload.board_roles)) {
    // Process each board role to handle the "Currently Working" flag
    const cleanedBoardRoles = newPayload.board_roles.map(role => ({
      ...role,
      // If currently working, clear the end date; otherwise keep it
      rep_end_date: role.is_currently_working ? "" : (role.rep_end_date || ""),
      // Ensure is_currently_working is a boolean
      is_currently_working: !!role.is_currently_working
    }));

    // Convert array of objects to JSON string
    newPayload.board_roles = JSON.stringify(cleanedBoardRoles);
  }

  return newPayload;
};

const cleanupPayloadFields = (payload) => {
  const newPayload = { ...payload };

  if (
    newPayload.mainArea === "undefined" ||
    newPayload.mainArea === "null" ||
    !newPayload.mainArea
  ) {
    delete newPayload.mainArea;
  }

  if (
    !newPayload.domains ||
    newPayload.domains === "undefined" ||
    newPayload.domains === "null" ||
    newPayload.domains === null
  ) {
    delete newPayload.domains;
  }

  if (
    newPayload.language === "undefined" ||
    newPayload.language === "null" ||
    !newPayload.language
  ) {
    delete newPayload.language;
  }

  return newPayload;
};

const handleResumeInPayload = (payload, selectedResume) => {
  const newPayload = { ...payload };
  if (
    selectedResume &&
    selectedResume instanceof File &&
    selectedResume.name &&
    selectedResume.size > 0 &&
    selectedResume.type === "application/pdf"
  ) {
    newPayload.resume = selectedResume;
  } else if ("resume" in newPayload) {
    const { resume, ...rest } = newPayload;
    return rest;
  }
  return newPayload;
};

const appendImage = (formData, payload, selectedFile) => {
  if (payload.image instanceof FileList && payload.image.length > 0) {
    formData.append("image", payload.image[0]);
    return;
  }

  if (payload.image instanceof File) {
    formData.append("image", payload.image);
    return;
  }

  if (selectedFile instanceof File) {
    formData.append("image", selectedFile);
  }
};

const shouldDeleteImage = (payload) =>
  "image" in payload &&
  !(payload.image instanceof File || payload.image instanceof FileList);

const getMergedKeys = (allowedKeys) => {
  let mergedKeys = [
    ...allowedKeys,
    "step_number",
    "type",
    "role_type",
    "email_verify_string",
  ];

  if (allowedKeys.includes("domains")) {
    mergedKeys = [...mergedKeys, "subareas", "subarea", "mainArea"];
  }

  if (allowedKeys.includes("availability")) {
    mergedKeys = [...mergedKeys, "from_date", "to_date"];
  }

  const hasBoardRoleFields = [
    "rep_company_name",
    "rep_board_title",
    "rep_start_date",
    "rep_end_date",
  ].every((key) => allowedKeys.includes(key));

  if (hasBoardRoleFields) {
    mergedKeys = [...mergedKeys, "board_roles"];
  }

  return mergedKeys;
};

const isValidValue = (value) =>
  value !== undefined &&
  value !== null &&
  value !== "" &&
  value !== "undefined";

export const buildRegisterFormData = ({
  data,
  registerStepFields,
  selectedFile,
}) => {
  const formData = new FormData();
  const payload = { ...data };

  appendImage(formData, payload, selectedFile);

  if (shouldDeleteImage(payload)) {
    delete payload.image;
  }

  const registerStep = registerStepFields?.[0]?.reg_feilds || [];
  const allowedKeys = registerStep.map((field) => field.reg_key);

  const mergedKeys = getMergedKeys(allowedKeys);

  mergedKeys.forEach((key) => {
    const value = payload[key];

    if (!isValidValue(value)) return;

    if (key === "board_roles" && Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

export const applyVerificationDataImmediately = ({
  data,
  setValue,
  setRoleId,
  handleChange,
  setEmail,
}) => {
  setValue("role_id", data?.role_id);
  setRoleId(data?.role_id);
  handleChange(data?.role_id);
  setEmail(data?.email);
  setValue("email", data?.email);
};

const setArrayField = (data, field, setValue, clearErrors, parser = Number) => {
  if (!data[field]) return;

  const arr = data[field]
    .split(",")
    .map((v) => parser(v.trim()));

  setValue(field, arr);
  clearErrors(field);
};

const setSimpleFields = (data, fields, setValue) => {
  fields.forEach((field) => {
    if (data[field] !== undefined) {
      setValue(field, data[field]);
    }
  });
};

const handleBoardRoles = (boardRolesData, setValue, clearErrors) => {
  if (!boardRolesData) return;

  try {
    let boardRolesArray = [];

    if (typeof boardRolesData === "string") {
      boardRolesArray = JSON.parse(boardRolesData);
    } else if (Array.isArray(boardRolesData)) {
      boardRolesArray = boardRolesData;
    }

    const formattedRoles = boardRolesArray.map((role) => ({
      rep_company_name: role.rep_company_name || "",
      rep_board_title: role.rep_board_title || "",
      rep_start_date: role.rep_start_date || "",
      rep_end_date: role.rep_end_date || "",
      is_currently_working: !!role.is_currently_working,
    }));

    setValue("board_roles", formattedRoles);
    clearErrors("board_roles");
  } catch (error) {
    console.error("Error parsing board_roles:", error);
    setValue("board_roles", []);
  }
};

export const applyVerificationDataWithDelay = ({
  data,
  reset,
  setValue,
  clearErrors,
  setSelectedFile,
  setImg,
  setResumeText,
  setSelectedResume,
  setShowDateDiv,
  checkFromDate,
  checkToDate,
  setDob,
  placeholderImage,
}) => {
  if (!data) return;

  const { image, resume, password, ...rest } = data;

  reset(rest);

  // Image
  setSelectedFile(image);
  setImg({
    src: image || placeholderImage || null,
    alt: "Upload an Image",
  });

  if (image && typeof image === "string") {
    setValue("image", image, { shouldValidate: false });
    clearErrors("image");
  }

  // Resume
  if (resume) {
    setValue("resume", resume);
    setResumeText(resume.split("/").pop());
    setSelectedResume(resume);
  }

  // Boolean fields
  [
    "mn_open_to_talk",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
  ].forEach((field) => {
    if (data[field] !== undefined) {
      setValue(field, !!Number(data[field]));
      clearErrors(field);
    }
  });

  // Array fields
  if (data.language) setValue("language", data.language.split(","));

  [
    "experience_levels",
    "board_entity_type",
    "board_entity_type_2",
    "course_completed",
    "accredited_director",
    "indicate_board_experience_level",
  ].forEach((field) =>
    setArrayField(data, field, setValue, clearErrors)
  );

  if (data.past_board_roles) {
    setValue(
      "past_board_roles",
      data.past_board_roles.split(",").map((v) => v.trim())
    );
    clearErrors("past_board_roles");
  }

  // Board roles repeater
  handleBoardRoles(data.board_roles_data, setValue, clearErrors);

  // Availability
  if (data.availability === 2) {
    setValue("availability", true);
    setShowDateDiv("block");
    setValue("from_date", checkFromDate(data.from_date));
    setValue("to_date", checkToDate(data.to_date));
  } else {
    setValue("availability", false);
    setValue("from_date", null);
    setValue("to_date", null);
    setShowDateDiv("none");
  }

  // DOB
  if (data.dob) {
    setValue("dob", data.dob);
    setDob(data.dob);
  }

  // Simple fields
  setSimpleFields(
    data,
    [
      "linkedin",
      "first_name",
      "last_name",
      "number",
      "university_partners_id",
      "program_study_id",
      "year_of_graduation",
      "organization",
      "position",
      "about",
      "is_terms",
      "is_privacy",
      "industry_id",
      "training_video",
      "years_of_experience",
      "current_course",
      "gender",
      "country",
      "lgbtq_discussion",
      "topics",
      "my_consent",
      "any_particular_program",
      "frequency_communication",
      "employee",
      "sim_student_id",
      "programmes",
      "number_of_mantees",
      "years_of_board_experience",
    ],
    setValue
  );
};

export const handlePostVerificationEffects = ({
  data,
  res,
  setReadOnly,
  setEmailVerifyString,
  getObjectiveData,
  registerSetting,
  setUserId,
  setPopupMessage,
  setShowPopup,
  history,
  renderGlobalModal,
}) => {
  setReadOnly(true);
  setEmailVerifyString(data?.email_verify_string);
  getObjectiveData(data?.role_id);
  registerSetting(data?.role_id);
  setUserId(data?.id);

  localStorage.setItem("email_verify", data?.email_verify_string);

  setPopupMessage(res?.message);
  setShowPopup(true);

  const shouldRedirect =
    data?.email_verfication_status === 1 &&
    data?.applicable_for_approval === 1 &&
    data?.role_id;

  if (shouldRedirect) {
    if (renderGlobalModal) {
      renderGlobalModal({ message: res?.message });
    }

    setTimeout(() => {
      history("/");
    }, 0);
  }
};

export const buildValidationRules = (validations, is_required, reg_key, label = "") => {
  const rules = {};

  for (const validation of validations) {
    switch (validation?.key) {
      case "required":
        if (is_required === 0) {
          break;
        } else {
          rules.required =
            validation.message
              ?.replaceAll(/<[^>]*>/g, "")
              ?.replaceAll("*", "")
              ?.trim() ||
            `${label} is required`;
          break;
        }
      case "min":
      case "minlength":
        rules.minLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "max":
      case "maxlength":
        rules.maxLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "pattern":
        rules.pattern = {
          value: new RegExp(validation?.regex?.slice(1, -1)),
          message: validation.message,
        };
        break;
      case "maxCount":
        rules.validate = (value) => {
          if (Array.isArray(value) && value?.length > validation?.length) {
            return validation?.message;
          }
          return true;
        };
        break;
      case "size":
        rules.validate = {
          ...rules.validate,
          fileSize: (files) =>
            files?.length > 0
              ? files[0]?.size < validation?.length * 1024 ||
              validation?.message ||
              "File size exceeds the allowed limit"
              : true,
        };
        break;
      case "mimes": {
        const allowedMimeTypes =
          reg_key === "resume" || reg_key === "document"
            ? validation?.types?.map((ext) => `application/${ext}`)
            : validation?.types?.map((ext) =>
              ext === "jpg" ? "image/jpeg" : `image/${ext}`
            );
        rules.validate = {
          ...rules.validate,
          fileType: (files) => {
            if (files?.length > 0) {
              return (
                allowedMimeTypes.includes(files[0].type) ||
                validation.message ||
                "Invalid file type"
              );
            }
            return true;
          },
        };
        break;
      }
      default:
        break;
    }
  }
  return rules;
};
